import { useEffect, useState } from 'react';

export interface QuickGuideItem {
  id: string;
  icon: string;
  label: string;
  description: string;
  colorClass: string;
  backgroundClass: string;
}

export function FirstLoginQuickGuide({ roleKey, title, items }: { roleKey: string; title: string; items: QuickGuideItem[] }) {
  const storageKey = `agrida_quick_guide_${roleKey}_v2`;
  const activeGuideKey = 'agrida_quick_guide_active_role';
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey) !== 'done') {
        const activeRole = sessionStorage.getItem(activeGuideKey);
        if (activeRole && activeRole !== roleKey) return;
        sessionStorage.setItem(activeGuideKey, roleKey);
        const timer = window.setTimeout(() => {
          setStep(0);
          setOpen(true);
        }, 650);
        return () => window.clearTimeout(timer);
      }
    } catch {
      return;
    }
  }, [activeGuideKey, roleKey, storageKey]);

  const close = () => {
    try { localStorage.setItem(storageKey, 'done'); } catch { /* ignore */ }
    try {
      if (sessionStorage.getItem(activeGuideKey) === roleKey) sessionStorage.removeItem(activeGuideKey);
    } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open || items.length === 0) return null;
  const item = items[step];
  const last = step === items.length - 1;

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/50 backdrop-blur-[2px] sm:items-center sm:p-4 animate-modal-backdrop">
      <section className="w-full overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:max-w-md sm:rounded-3xl animate-modal-slide-up">
        <div className="bg-gradient-to-br from-agrida-700 via-agrida-600 to-teal-500 px-5 pb-5 pt-6 text-white">
          <div className="flex items-start justify-between gap-3">
            <div><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/70">Lần đăng nhập đầu tiên</p><h2 className="mt-1 text-lg font-bold">{title}</h2></div>
            <button type="button" onClick={close} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25" aria-label="Đóng hướng dẫn"><i className="ri-close-line" /></button>
          </div>
          <div className="mt-4 flex gap-1.5">{items.map((guideItem, index) => <span key={guideItem.id} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${index <= step ? 'bg-white' : 'bg-white/25'}`} />)}</div>
        </div>

        <div className="px-5 py-6 text-center">
          <span className={`mx-auto flex h-16 w-16 items-center justify-center rounded-2xl ${item.backgroundClass}`}><i className={`${item.icon} ${item.colorClass} text-2xl`} /></span>
          <p className="mt-4 text-[10px] font-bold uppercase tracking-wide text-slate-400">Thao tác {step + 1}/{items.length}</p>
          <h3 className="mt-1 text-lg font-bold text-slate-900">{item.label}</h3>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-500">{item.description}</p>

          <div className="mt-6 flex gap-2.5">
            <button type="button" onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={step === 0} className="min-h-11 flex-1 rounded-xl bg-slate-100 px-3 text-sm font-semibold text-slate-600 transition disabled:opacity-35">Quay lại</button>
            <button type="button" onClick={() => last ? close() : setStep((current) => current + 1)} className="min-h-11 flex-[1.5] rounded-xl bg-agrida-700 px-3 text-sm font-bold text-white shadow-lg shadow-agrida-700/20 transition hover:bg-agrida-800 active:scale-[0.98]">{last ? 'Bắt đầu sử dụng' : 'Tiếp theo'}</button>
          </div>
        </div>
      </section>
    </div>
  );
}
